import { SMART_LIST_REVISIONS_MAX, type FilterCondition, type LeadCatalogResponse, type SmartListDetail } from "@gioia365/contracts";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent, type JSX } from "react";
import { ApiRequestError } from "../api/http";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { MenuItem } from "../design/components/MenuItem";
import { useMenuButton } from "../design/components/menu-button";
import { Reason } from "../design/components/Reason";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatNumericDate } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import type { ColumnDef } from "../leads/columns/table-layout";
import { filterLabelQueries, filterValueLabels } from "../leads/filters/filter-queries";
import { filterFields } from "../leads/filters/filter-text";
import type { TabContext } from "../session/tab-context";
import { useRenameSmartList } from "./smart-list-queries";
import type { ChangeFields } from "./smart-list-text";
import "./smart-lists.css";

// Le finestre e il menu della pagina di una Smart List (piano M3 Task 9, mock-up `smart-list.html`): «Gestisci lista» con
// «Rinomina», «Storico della lista» ed «Elimina la lista»; «Cosa cambia per chi la usa» prima di salvare una lista che
// usano anche altri; «Non hai salvato le modifiche» quando si esce con una bozza; «Rinomina la lista» ed «Elimina la
// lista». «Condividi» e lo storico hanno i loro file.

/** Un giorno di calendario (`AAAA-MM-GG`) non ha fuso: resta quel giorno ovunque (come nei filtri attivi). */
const formatDay = (day: string) => formatNumericDate("UTC", day);

/**
 * Quello che serve per dire delle differenze in parole (`describeChanges`), con i nomi delle voci delle condizioni date
 * letti dal server se la scheda non li ricorda ancora (decisione P20): la finestra si ridisegna quando arrivano.
 */
export function useChangeFields(input: { tab: TabContext; catalog: LeadCatalogResponse | undefined; columns: readonly ColumnDef[]; conditions: readonly FilterCondition[] }): ChangeFields {
  const { tab, catalog, columns, conditions } = input;
  const t = useT();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  useQueries({ queries: filterLabelQueries(queryClient, tab, conditions) });
  const valuesOf = (ref: string) => conditions.flatMap((condition) => (condition.field === ref && "values" in condition ? condition.values : []));
  return {
    filterFields: filterFields(t, catalog),
    catalog,
    columns,
    labelsOf: (ref) => filterValueLabels(t, queryClient, tab, ref, valuesOf(ref), catalog),
    formatDay,
    locale,
  };
}

export type MoreAction = "rename" | "history" | "delete";

/**
 * «Gestisci lista» (mock-up `openMore`): un menu sotto il pulsante con le voci che chi guarda può usare, ognuna con il suo perché;
 * senza «Rinomina» o senza «Elimina la lista» la nota dice chi può fare ciò che manca. Frecce, Inizio e Fine si muovono fra
 * le voci, Esc chiude e torna al pulsante, un clic fuori chiude (`useMenuButton`, lo stesso di «Gestisci lista» della scheda).
 */
export function MoreMenu({ list, onChoose }: { list: SmartListDetail; onChoose: (action: MoreAction) => void }): JSX.Element {
  const t = useT();
  const { open, close, wrapperRef, buttonRef, menuRef, buttonProps, menuProps } = useMenuButton();
  const { canRename, canDelete } = list.permissions;

  const items: Array<{ action: MoreAction; label: string; hint: string }> = [];
  if (canRename) items.push({ action: "rename", label: t("smartLists.page.rename"), hint: t("smartLists.page.renameHint") });
  // Lo storico mostra le ultime cento versioni: il pulsante non ne promette di più.
  const versions = Math.min(list.revision, SMART_LIST_REVISIONS_MAX);
  items.push({
    action: "history",
    label: t("smartLists.page.history"),
    hint: versions === 1 ? t("smartLists.page.historyHintOne") : t("smartLists.page.historyHint", { count: versions }),
  });
  if (canDelete) items.push({ action: "delete", label: t("smartLists.page.delete"), hint: t("smartLists.page.deleteHint") });
  // Chi può fare ciò che manca, con i ruoli di partenza: rinominare è modificare (`update`), che il Super user ha su tutte le
  // liste; eliminare è `delete`, che il Super user ha soltanto sulle sue (`smart-lists/access.ts`, ruoli di partenza).
  const owner = list.owner.displayName;
  let note: string | null = null;
  if (!canRename && !canDelete) note = t("smartLists.page.moreNote", { owner });
  else if (!canDelete) note = t("smartLists.page.moreNoteDelete", { owner });
  else if (!canRename) note = t("smartLists.page.moreNoteRename", { owner });

  function choose(action: MoreAction) {
    // La finestra che si apre ridà poi il fuoco ad «Gestisci lista», che resta nella pagina.
    close(true);
    onChoose(action);
  }

  return (
    <span className="g-smart-more" ref={wrapperRef}>
      <Button ref={buttonRef} {...buttonProps}>
        {t("smartLists.page.more")}
        <Icon name="down" />
      </Button>
      {open ? (
        <div ref={menuRef} {...menuProps} aria-label={t("smartLists.page.moreLabel")} className="g-smart-more__menu">
          {items.map((item) => (
            <MenuItem key={item.action} label={item.label} hint={item.hint} onClick={() => choose(item.action)} />
          ))}
          {note ? (
            <span role="menuitem" aria-disabled="true" tabIndex={-1} className="g-smart-more__note">
              {note}
            </span>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}

type ConfirmProps = {
  /** Chi la usa: «Sara Conti e Luca Neri», «tutti in Academy, perché è pubblica al team». */
  people: string;
  lines: readonly string[];
  /** I Lead della lista salvata e della bozza, per chi guarda; mentre arrivano, «Un momento…». */
  before: string | null;
  after: string | null;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
};

/** «Cosa cambia per chi la usa» (mock-up `openSaveConfirm`): prima di salvare una lista condivisa o pubblica. */
export function SaveConfirmDialog({ people, lines, before, after, saving, onCancel, onSave }: ConfirmProps): JSX.Element {
  const t = useT();
  return (
    <Dialog
      open
      title={t("smartLists.page.confirmTitle")}
      onClose={() => {
        if (!saving) onCancel();
      }}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving} aria-busy={saving ? "true" : undefined}>
            {t("smartLists.page.confirmSave")}
          </Button>
        </>
      }
    >
      <p>{t("smartLists.page.confirmUsers", { people })}</p>
      <ul className="g-smart-changes">
        {lines.map((line, index) => (
          <li key={`${index}-${line}`}>{line}</li>
        ))}
      </ul>
      <p>{before !== null && after !== null ? t("smartLists.page.confirmCount", { before, after }) : t("common.loading")}</p>
      <p className="g-new-list__note">{t("smartLists.page.confirmHistory")}</p>
    </Dialog>
  );
}

type LeaveProps = { canSave: boolean; canCreate: boolean; onStay: () => void; onLeave: () => void; onSaveAs: () => void; onSaveAndLeave: () => void };

/** «Non hai salvato le modifiche» (mock-up `#m-leave-list`): restare, uscire senza salvare, salvarla come nuova o salvare ed uscire. */
export function LeaveListDialog({ canSave, canCreate, onStay, onLeave, onSaveAs, onSaveAndLeave }: LeaveProps): JSX.Element {
  const t = useT();
  return (
    <Dialog
      open
      title={t("smartLists.page.leaveTitle")}
      onClose={onStay}
      footer={
        <>
          <Button onClick={onStay}>{t("smartLists.page.stay")}</Button>
          <Button onClick={onLeave}>{t("smartLists.page.leaveDiscard")}</Button>
          {canCreate ? <Button onClick={onSaveAs}>{t("smartLists.page.saveAs")}</Button> : null}
          {canSave ? (
            <Button variant="primary" onClick={onSaveAndLeave}>
              {t("smartLists.page.saveAndLeave")}
            </Button>
          ) : null}
        </>
      }
    >
      <p>{t("smartLists.page.leaveText")}</p>
    </Dialog>
  );
}

/** Il massimo del nome (`renameSmartListBodySchema`): oltre, il server risponderebbe 422. */
const NAME_MAX_LENGTH = 120;

type RenameProps = { tab: TabContext; list: SmartListDetail; me: string | null; onReload: () => unknown; onClose: () => void };

/** «Rinomina la lista»: lo stesso nome non scrive nulla; un nome che l'owner ha già in un'altra lista lo dice sul campo. */
export function RenameDialog({ tab, list, me, onReload, onClose }: RenameProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const baseId = useId();
  const formId = `${baseId}-form`;
  const rename = useRenameSmartList(tab, list.id);
  const [name, setName] = useState(list.name);
  const [problem, setProblem] = useState<{ field: "name" | "form"; text: (tr: Translate) => string } | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.select();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (rename.isPending) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setProblem({ field: "name", text: (tr) => tr("smartLists.nameRequired") });
      nameRef.current?.focus();
      return;
    }
    if (trimmed === list.name) {
      onClose();
      return;
    }
    setProblem(null);
    try {
      const renamed = await rename.mutateAsync({ expectedRevision: list.revision, name: trimmed });
      toast.show(t("smartLists.page.renamed", { name: renamed.name }), "success");
      onClose();
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "smart_list_name_taken") {
        // Il nome è unico nel menu dell'owner: per una lista di un altro, è il suo menu.
        const owner = list.owner.membershipId === me ? null : list.owner.displayName;
        setProblem({ field: "name", text: (tr) => (owner ? tr("smartLists.nameTakenFor", { name: owner }) : tr("smartLists.nameTaken")) });
        nameRef.current?.focus();
        return;
      }
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        void onReload();
        setProblem({ field: "form", text: (tr) => tr("smartLists.page.changedMeanwhile") });
        return;
      }
      setProblem({ field: "form", text: (tr) => messageForError(tr, error) });
    }
  }

  return (
    <Dialog
      open
      title={t("smartLists.page.renameTitle")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={rename.isPending} aria-busy={rename.isPending ? "true" : undefined}>
            {t("smartLists.page.rename")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void submit(event)} noValidate>
        <TextField
          id={`${baseId}-name`}
          ref={nameRef}
          label={t("smartLists.page.renameName")}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          autoComplete="off"
          error={problem?.field === "name" ? problem.text(t) : undefined}
          onChange={(event) => setName(event.target.value)}
        />
        <p className="g-new-list__note">{t("smartLists.page.renameNote")}</p>
        {problem?.field === "form" ? (
          <p className="g-field__error" role="alert">
            {problem.text(t)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

type DeleteProps = { name: string; deleting: boolean; error: unknown; onCancel: () => void; onConfirm: () => void };

/** «Elimina la lista»: la conferma con il nome; ai Lead non succede niente. */
export function DeleteDialog({ name, deleting, error, onCancel, onConfirm }: DeleteProps): JSX.Element {
  const t = useT();
  return (
    <Dialog
      open
      title={t("smartLists.page.deleteTitle")}
      onClose={() => {
        if (!deleting) onCancel();
      }}
      footer={
        <>
          <Button onClick={onCancel} disabled={deleting}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={deleting} aria-busy={deleting ? "true" : undefined}>
            {t("smartLists.page.deleteConfirm")}
          </Button>
        </>
      }
    >
      <p>{t("smartLists.page.deleteText", { name })}</p>
      {error ? (
        <Reason tone="warning" role="alert">
          {messageForError(t, error)}
        </Reason>
      ) : null}
    </Dialog>
  );
}
